import { Platform } from "react-native";
import PublicClientApplication from "react-native-msal";

// Declare variables to be used in mobile context
// Do not import from @env here - will be loaded conditionally only for mobile platforms
// No need to declare these variables anymore since we're using a different approach

// Add type declaration for the global MSAL config window property
interface CustomWindow extends Window {
  _msalConfig?: {
    clientId: string;
    authority: string;
    redirectUri: string;
  };
}

// Singleton pattern for MSAL instances
let webMsalInstance: any = null;
let mobileMsalInstance: any = null;

export const getMsalInstance = async () => {
  if (Platform.OS === "web") {
    if (!webMsalInstance) {
      const { PublicClientApplication } = require("@azure/msal-browser");
      
      // For web, always use the values defined in webpack's process.env
      // Determine redirect URI based on environment
      let redirectUri = process.env.MSAL_REDIRECT_URI_WEB || '';
      
      // For local development, override to use current hostname
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        redirectUri = `${window.location.protocol}//${window.location.host}/`;
        console.log("🔍 Local development detected, using redirect URI:", redirectUri);
      }
      
      // For web platform, only use webpack process.env values
      // These values are injected by webpack.DefinePlugin and not from .env
      // If process.env values are not available, we'll use empty strings and log error
      const clientId = process.env.MSAL_CLIENT_ID || "";
      const authority = process.env.MSAL_AUTHORITY || "";
      
      // Log error if values are missing
      if (!clientId) console.error("❌ MSAL Client ID not found in webpack config");
      if (!authority) console.error("❌ MSAL Authority not found in webpack config");
      
      // Store configuration in global for debugging
      (window as CustomWindow)._msalConfig = {
        clientId: clientId,
        authority: authority,
        redirectUri: redirectUri
      };
      
      console.log("🔍 MSAL Configuration (from webpack):");
      console.log("Client ID:", clientId);
      console.log("Authority:", authority);
      console.log("Redirect URI:", redirectUri);
      
      webMsalInstance = new PublicClientApplication({
        auth: {
          clientId: clientId,
          authority: authority,
          redirectUri: redirectUri,
        },
        cache: {
          cacheLocation: "localStorage",
          storeAuthStateInCookie: true,
        },
        system: {
          loggerOptions: {
            loggerCallback: (level: any, message: string) => {
              if (level > 2) {
                console.log(`MSAL: ${message}`);
              }
            },
            piiLoggingEnabled: false,
          },
        },
      });

      await webMsalInstance.initialize();
      
      // Don't handle redirect promise here - let the provider handle it
      // to avoid double handling and redirect loops
    }
    return webMsalInstance;
  } else {
    if (!mobileMsalInstance) {
      // Only for mobile platforms
      
      // For mobile platforms, use a special approach to access environment variables
      // that doesn't require webpack to resolve @env
      
      // Default values as fallbacks
      let clientId = '2db662d1-27f1-432f-89b3-3efa2309d2dc';
      // let clientId = 'e3a34560-5d71-4055-b2d7-6ab366704411';
      let authority = 'https://login.microsoftonline.com/42cc3295-cd0e-449c-b98e-5ce5b560c1d3/v2.0';
      let redirectAndroid = 'msauth://com.lighthouse/IAUhKz8OCHYYP0k/PFkyPz8/Q24NCg==';
      // let redirectIos = 'msauth.com.pepsico.navi://auth';
      let redirectIos = 'msauth.com.pepsico.lighthouse.dev://auth';
      // In the metro bundling process for mobile, these values will come from .env
      // We'll use Function constructor to dynamically evaluate the code
      // This prevents webpack from statically analyzing and trying to resolve @env
      try {
        // Check if we're on mobile platform first (outside of Function constructor)
        // @ts-ignore - Platform.OS can be 'web' in web builds
        const isMobile = Platform.OS !== "web";
        
        // This code will only run on mobile and is safe from webpack
        const dynamicImport = new Function('return typeof global !== "undefined" && global.MSAL_CLIENT_ID ? global.MSAL_CLIENT_ID : ""')();
        
        // If we get a value from global variables set by metro, use them
        if (isMobile && dynamicImport) {
          console.log("✅ Using environment variables from metro bundler");
          
          // These globals are set by metro bundler during the mobile build process
          // @ts-ignore - TypeScript doesn't know about these globals
          clientId = global.MSAL_CLIENT_ID || clientId;
          // @ts-ignore
          authority = global.MSAL_AUTHORITY || authority;
          // @ts-ignore
          redirectAndroid = global.MSAL_REDIRECT_URI_ANDROID || redirectAndroid;
          // @ts-ignore
          redirectIos = global.MSAL_REDIRECT_URI_IOS || redirectIos;
        }
        
        console.log("✅ Mobile MSAL config:", {
          clientId: clientId ? clientId.substring(0, 5) + "..." : "MISSING",
          authority: authority ? "SET" : "MISSING",
          redirectAndroid: redirectAndroid ? "SET" : "MISSING",
          redirectIos: redirectIos ? "SET" : "MISSING"
        });
      } catch (error) {
        console.error("❌ Error configuring mobile environment:", error);
      }
        
      mobileMsalInstance = new PublicClientApplication({
        auth: {
          clientId: clientId,
          authority: authority,
          redirectUri: Platform.select({
            android: redirectAndroid,
            ios: redirectIos,
          }),
        },
      });

      try {
        await mobileMsalInstance.init();
        console.log("✅ Mobile MSAL initialized successfully");
      } catch (error) {
        console.error("❌ Failed to initialize mobile MSAL:", error);
      }
    }
    return mobileMsalInstance;
  }
};

export const clearMsalInstances = () => {
  webMsalInstance = null;
  mobileMsalInstance = null;
};